// ─────────────────────────────────────────────────────────────────────────────
// The shell.
//
// There is ONE application. This file draws it, and what it draws comes from
// `workspace.navigation` — the server's answer, computed by the pure model in
// @domain/workspace/navigation.
//
// The important property: the menu and the router are built from the SAME list,
// in one pass. There is no hardcoded `NAV` sitting beside a hardcoded `ROUTES`
// that can drift apart — the previous version had exactly that, and it let an
// employee reach /payroll and /mail because the two lists disagreed.
//
// A route that is not in the model is not registered at all. Not rendered
// behind a guard: absent. An employee's browser has no /payroll route to visit.
// The server refuses those endpoints independently, so this is the second lock
// rather than the only one.
// ─────────────────────────────────────────────────────────────────────────────

import { Suspense, lazy, useState, type ComponentType, type ReactElement } from "react";
import { Link, Redirect, Route, Switch, useLocation } from "wouter";
import { AlbaLogo } from "./AlbaLogo";
import { PERMISSIONS } from "@shared/rbac";
import { trpc } from "../api";
import { shownName } from "@shared/names";
import { useI18n, type TranslationKey } from "../i18n";
import { ErrorNotice, Loading } from "../ui";
import { ChevronIcon, NavIcon } from "../ui/icons";
import { useSession } from "./session";
import { TopbarClock } from "./TopbarClock";
import { NotificationBell } from "../features/mail/NotificationBell";
import { SignInPage } from "../features/auth/SignInPage";
import { ChangePasswordPage } from "../features/auth/ChangePasswordPage";
import { ForgotPasswordPage } from "../features/auth/ForgotPasswordPage";
import { ResetPasswordPage } from "../features/auth/ResetPasswordPage";

const MyDashboard    = lazy(() => import("../features/dashboard/EmployeeDashboardPage"));
const MyProfile      = lazy(() => import("../features/me/MyProfilePage"));
const Notifications  = lazy(() => import("../features/me/NotificationsPage"));
const MyPerformance  = lazy(() => import("../features/performance/MyPerformancePage"));
const TasksCalendar  = lazy(() => import("../features/tasks/CalendarPage"));
const TasksDay       = lazy(() => import("../features/tasks/DayPage"));
const TasksList      = lazy(() => import("../features/tasks/TasksPage"));
const AttendancePage = lazy(() => import("../features/attendance/AttendancePage"));
const LeavePage      = lazy(() => import("../features/leave/LeavePage"));
const RequestsPage   = lazy(() => import("../features/requests/RequestsPage"));
const Dashboard      = lazy(() => import("../features/dashboard/DashboardPage"));
const EmployeeList   = lazy(() => import("../features/employees/EmployeeListPage"));
const EmployeeDetail = lazy(() => import("../features/employees/EmployeeDetailPage"));
// Hiring lives in the contracts feature, not employees: the Employee page is a
// read-only directory and audit rule 30 forbids a write there.
const HirePage       = lazy(() => import("../features/contracts/HirePage"));
const ContractPage   = lazy(() => import("../features/contracts/ContractPage"));
const StampPage      = lazy(() => import("../features/stamp/StampPage"));
const PayrollPage    = lazy(() => import("../features/payroll/PayrollPage"));
const PayrollRunPage = lazy(() => import("../features/payroll/PayrollRunPage"));
const DocumentsPage  = lazy(() => import("../features/documents/DocumentsPage"));
const DocumentView   = lazy(() => import("../features/documents/DocumentViewPage"));
const LoansPage      = lazy(() => import("../features/loans/LoansPage"));
const LoanEmployeePage = lazy(() => import("../features/loans/LoanEmployeePage"));
const MailPage       = lazy(() => import("../features/mail/MailPage"));
const Performance    = lazy(() => import("../features/performance/PerformancePage"));
const ReportsPage    = lazy(() => import("../features/reports/ReportsPage"));
const OrgChartPage   = lazy(() => import("../features/orgchart/OrgChartPage"));
const AssistantPage  = lazy(() => import("../features/ai/AssistantPage"));
const MailSettings   = lazy(() => import("../features/mail/MailSettingsPage"));
const SettingsPage   = lazy(() => import("../features/settings/SettingsPage"));
const BranchPage     = lazy(() => import("../features/settings/BranchPage"));

/**
 * What each section looks like: its label, its page, and the pages of any
 * detail routes that belong to it.
 *
 * Presentation only. Which sections a person GETS is decided by the model on
 * the server; this table just says how to draw the ones they got. Every id here
 * must exist in `SECTIONS`, and `npm run audit` fails the build otherwise —
 * that check is what keeps this table from becoming a second source of truth.
 */
interface SectionView {
  readonly label: TranslationKey;
  readonly component: ComponentType;
  readonly children?: Readonly<Record<string, ComponentType>>;
}

const VIEWS: Readonly<Record<string, SectionView>> = {
  myDashboard:   { label: "common.navMyDashboard", component: MyDashboard },
  profile:       { label: "common.navProfile",      component: MyProfile },
  notifications: { label: "common.navNotifications", component: Notifications },
  myPerformance: { label: "common.navMyPerformance", component: MyPerformance },
  calendar:      { label: "common.navCalendar",     component: TasksCalendar, children: { "/calendar/day/:date": TasksDay } },
  tasks:         { label: "common.navTasks",        component: TasksList },
  attendance:    { label: "common.navAttendance",   component: AttendancePage },
  leave:         { label: "common.navLeave",        component: LeavePage },
  requests:      { label: "common.navRequests",     component: RequestsPage },
  dashboard:     { label: "common.navManagementDashboard", component: Dashboard },
  employees:     { label: "common.navEmployees",    component: EmployeeList, children: { "/hire": HirePage, "/contracts/:id": ContractPage, "/employees/:id": EmployeeDetail } },
  payroll:       { label: "common.navPayroll",      component: PayrollPage, children: { "/payroll/:runId": PayrollRunPage } },
  documents:     { label: "common.navDocuments",    component: DocumentsPage, children: { "/documents/:id": DocumentView } },
  loans:         { label: "common.navLoans",        component: LoansPage, children: { "/loans/:employeeId": LoanEmployeePage } },
  mail:          { label: "common.navMail",         component: MailPage },
  stamp:         { label: "common.navStamp",        component: StampPage },
  performance:   { label: "common.navPerformance",  component: Performance },
  reports:       { label: "common.navReports",      component: ReportsPage },
  orgchart:      { label: "common.navOrgChart",     component: OrgChartPage },
  assistant:     { label: "common.navAssistant",    component: AssistantPage },
  mailSettings:  { label: "common.navMailSettings", component: MailSettings },
  settings:      { label: "common.navSettings",     component: SettingsPage, children: { "/settings/branches/:branchId": BranchPage } },
};



const SIDEBAR_COLLAPSED_KEY = "alba.sidebarCollapsed";
const readStoredCollapsed = (): boolean =>
  typeof window !== "undefined" && window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "1";

export function Shell(): ReactElement {
  const session = useSession();
  const { t, locale, setLocale } = useI18n();
  const [location] = useLocation();
  const [collapsed, setCollapsed] = useState<boolean>(readStoredCollapsed);

  const toggleSidebar = (): void => {
    setCollapsed((prev) => {
      const next = !prev;
      window.localStorage.setItem(SIDEBAR_COLLAPSED_KEY, next ? "1" : "0");
      return next;
    });
  };

  // THE query. One call decides the menu and the routes together.
  const navigation = trpc.workspace.navigation.useQuery(undefined, {
    staleTime: 60_000,
    retry: false,
    enabled: session.isAuthenticated,
  });

  if (session.isLoading) return <Loading />;
  // ── SIGNED-OUT ROUTES, BEFORE THE GATE ──────────────────────────────────
  //
  // A reset link is followed by somebody who cannot sign in — that is what the
  // link is for — so these two must be reachable with no session. wouter is
  // mounted below this point, so the check is on the raw location.
  //
  // The token is read from the query string and handed straight to the page. It
  // is not put in state, not stored, and not written to the URL by anything
  // here; the address bar already holds it and that is the only copy.
  if (!session.isAuthenticated) {
    if (location.startsWith("/reset-password")) {
      return (
        <ResetPasswordPage
          token={new URLSearchParams(window.location.search).get("token") ?? ""}
          onDone={() => { window.location.assign("/"); }}
          onRequestAnother={() => { window.location.assign("/forgot-password"); }}
        />
      );
    }
    if (location.startsWith("/forgot-password")) {
      return <ForgotPasswordPage onBack={() => { window.location.assign("/"); }} />;
    }
    return <SignInPage onSignedIn={session.refetch} onForgotPassword={() => { window.location.assign("/forgot-password"); }} />;
  }

  // A temporary password must be replaced before anything else is reachable.
  if (session.actor?.mustChangePassword === true) {
    return <ChangePasswordPage forced onChanged={session.refetch} />;
  }

  if (navigation.isLoading) return <Loading />;

  // Without the model there is no application to draw, so this cannot fall back
  // to "show everything" — that would be a leak dressed as resilience. It says
  // what went wrong and offers the two things that ever help.
  if (navigation.data === undefined) {
    return (
      <div className="content">
        <ErrorNotice error={navigation.error} />
        <div style={{ display: "flex", gap: "var(--space-2)", marginTop: "var(--space-3)" }}>
          <button type="button" className="btn btn-primary" onClick={() => void navigation.refetch()}>
            {t("common.retry")}
          </button>
          <SignOutButton onSignedOut={session.refetch} />
        </div>
      </div>
    );
  }

  const model = navigation.data;
  const sections = model.sections.filter((section) => VIEWS[section.id] !== undefined);

  // The sidebar order: each section in the model's order, with any section
  // drawn under it following immediately, indented.
  //
  // A nested section whose parent this person cannot see is NOT hidden — it
  // falls back to its own place in the list. `parent` is where to draw a thing,
  // never whether to draw it; that is settled by the permission alone, and
  // somebody with `stamp:use` and no `documents:read` still has the tool.
  const visibleIds = new Set(sections.map((section) => section.id));
  const menu = sections.flatMap((section) => {
    const nested = section.parent !== undefined && visibleIds.has(section.parent);
    if (nested) return [];
    const children = sections.filter((c) => c.parent === section.id);
    return [
      { section, nested: false },
      ...children.map((child) => ({ section: child, nested: true })),
    ];
  });

  return (
    <div className="app-shell">
      <nav className={collapsed ? "sidebar sidebar-collapsed" : "sidebar"} aria-label="Main">
        <div className="sidebar-top">
          <div className="sidebar-brand"><AlbaLogo height={26} tone="inverse" /></div>
          <button
            type="button"
            className="sidebar-toggle"
            onClick={toggleSidebar}
            aria-label={t(collapsed ? "common.expandSidebar" : "common.collapseSidebar")}
            title={t(collapsed ? "common.expandSidebar" : "common.collapseSidebar")}
          >
            <ChevronIcon direction={collapsed ? "end" : "start"} />
          </button>
        </div>

        {/* Ordered by importance in navigation.ts. Still no group headings:
            with twenty entries, asking the reader to decide which heading their
            task lives under is a cost paid on every visit. The one nesting that
            exists is a section drawn under another — Stamp under Documents —
            which is a position in the menu, not a grouping of the list. */}
        <div className="sidebar-nav">
          {menu.map(({ section, nested }) => {
            const label = t(VIEWS[section.id]?.label ?? "common.navMyDashboard");
            return (
              <Link
                key={section.id}
                href={section.path}
                className={nested ? "nav-link nav-link-nested" : "nav-link"}
                aria-current={location === section.path ? "page" : undefined}
                title={collapsed ? label : undefined}
              >
                <span className="nav-icon"><NavIcon id={section.id} /></span>
                <span className="nav-label">{label}</span>
              </Link>
            );
          })}
        </div>

        <div className="sidebar-footer">
          <div>{shownName(locale, { name: session.actor?.fullName ?? "", nameAr: session.actor?.fullNameAr ?? null })}</div>
          {/* ── THE ACCESS LEVEL, LABELLED, NOT THE JOB TITLE ───────────────
              This line used to be `jobTitle ?? role`, and the two are not the
              same thing at all. A branch manager whose job title is "General
              Dentist" read as ordinary staff, and five windows signed in as
              five different roles were indistinguishable — which is how Ali
              lost track of which window was which.

              The label goes WITH it for the same reason the bug existed:
              an unlabelled line in this position is read as whatever the
              reader expects, and putting access next to job title without
              naming both just moves the confusion somewhere new.

              `session.actor.role` rather than anything re-queried, because that
              is the value authorization itself uses — so what a person reads
              here cannot disagree with what the menu decided to show them.

              `--sidebar-ink-dim`, not an inline opacity. Opacity fades whatever
              is underneath it too and gives no measurable ratio to check; the
              variable is off-white at 0.78 over near-black, which is 10.90:1. */}
          {session.actor !== null && (
            <div className="sidebar-footer-role">
              {t("employees.role")}: {t(`enums.erpRole_${session.actor.role}`)}
            </div>
          )}
        </div>
      </nav>

      <div className="main">
        {/* Three columns, not a row with a spacer, so the middle one is
            genuinely centred rather than "wherever the two side groups leave
            it" — the brand and the action buttons are different widths, and
            with a spacer the middle would drift as either changed. */}
        <header className="topbar">
          {/* The clock joins the START track rather than becoming a fourth
              column, because a fourth column would move the middle one off the
              middle and the comment below is about why that middle matters.
              It is here rather than beside Sign out because it is status, not
              an action — and because that side is the one that runs out of
              room on a phone. See TopbarClock.tsx. */}
          <div className="topbar-lead">
            <strong className="topbar-brand">{t("common.appName")}</strong>
            <TopbarClock />
          </div>

          {/* THE LANGUAGE TOGGLE LIVES HERE FOR A REASON.
              It used to sit at the inline-end, beside Sign out. Switching
              language mirrors the whole shell, which moved the sidebar's 236px
              column from one edge of the window to the other — so the click
              AFTER the toggle, aimed at content that had been on the right,
              landed on a navigation link and navigated. That is the
              "the sidebar sometimes navigates when I switch language" report,
              and the toggle was never the thing doing it.
              The centre is the fix: whichever direction the layout is in, the
              point the pointer is left on is inert topbar space rather than a
              link. Do not move this back to the edge. */}
          <div className="topbar-centre">
            <button type="button" className="btn btn-sm" onClick={() => setLocale(locale === "en" ? "ar" : "en")}>
              {locale === "en" ? "العربية" : "English"}
            </button>
          </div>

          <div className="topbar-actions">
            {/* Everyone has notifications; not everyone has a Mail Center. */}
            {session.can(PERMISSIONS.MAIL_NOTIFICATIONS) && <NotificationBell />}
            <SignOutButton onSignedOut={session.refetch} />
          </div>
        </header>

        <main className="content">
          <Suspense fallback={<Loading />}>
            <Switch>
              {sections.flatMap((section) => {
                const view = VIEWS[section.id];
                if (view === undefined) return [];
                const Page = view.component;
                const routes = [
                  <Route key={section.path} path={section.path}><Page /></Route>,
                ];
                for (const [childPath, Child] of Object.entries(view.children ?? {})) {
                  routes.push(<Route key={childPath} path={childPath}><Child /></Route>);
                }
                return routes;
              })}
              {/* Anything else — including a page this person may not open —
                  goes home rather than announcing that it exists. */}
              <Route><Redirect to={model.home} /></Route>
            </Switch>
          </Suspense>
        </main>
      </div>
    </div>
  );
}

function SignOutButton({ onSignedOut }: { onSignedOut: () => void }): ReactElement {
  const { t } = useI18n();
  const signOut = trpc.auth.signOut.useMutation({
    onSuccess: () => { onSignedOut(); window.location.href = "/"; },
  });
  return (
    <button type="button" className="btn btn-sm" onClick={() => signOut.mutate()} disabled={signOut.isPending}>
      {t("common.signOut")}
    </button>
  );
}
